import { useCallback, useEffect, useState } from "react";
import type { Translate } from "@dnd-kit/core/dist/types/coordinates";
import { getGridSize } from "../../tools/ConfigurationReader";
import { lsLoad, lsSave } from "../../tools/Storage";
import StorageEnum from "../../enums/Storage.enum";
import { useTranslation } from "react-i18next";
import { useEditMarket } from "../../provider/edit-market/EditMarket.hook.ts";
import {
  type MarketComponentMarketComponentIO,
  MarketComponentMarketComponentIOMarketComponentStatusEnum,
  type MarketMarketIO,
} from "../../generated/fresh-guard-api";
import { hasCollision } from "../../tools/helpers/CollisionDetectionHelper";
import type { DragEndEvent } from "@dnd-kit/core";
import { useDragAndDrop } from "../../provider/drag-and-drop/DragAndDrop.hook.ts";
import { parseNextNumber } from "../../tools/converters/NumberConverter.ts";
import { useMarketsSelect } from "../../apis/market/useMarketsSelect.ts";
import { useMarketComponentsSelect } from "../../apis/market-component/useMarketComponentsSelect.ts";

export const useMainController = () => {
  const { t } = useTranslation();
  const {
    enableZoom,
    marketComponents,
    addMarketComponent,
    removeMarketComponent,
    initMarket,
    initMarketComponents,
    addIdOfMarketComponents,
    isInEditMode,
  } = useEditMarket();
  const [showDetails, setShowDetails] = useState(true);
  const [currentMarketComponent, setCurrentMarketComponent] =
    useState<MarketComponentMarketComponentIO>();
  const { marketComponent, setMarketComponent, transformState } =
    useDragAndDrop();
  const [currentMarket, setCurrentMarket] = useState<string>(
    lsLoad(StorageEnum.Market)?.id?.toString() ?? "",
  );
  const isDetailsDialogOpen = !!currentMarketComponent && showDetails;
  const isEditDialogOpen = !!currentMarketComponent && !showDetails;

  const { data: marketData, isLoading: marketLoading } = useMarketsSelect();
  const { data: marketComponentsData, isLoading: marketComponentsLoading } =
    useMarketComponentsSelect(currentMarket);

  const calculateNewPositionOfMarketComponent = (
    currentMarketComponent: MarketComponentMarketComponentIO,
    delta: Translate,
  ) => {
    const gridSize = getGridSize();

    const alignToGrid = (value: number) => {
      return (Math.round((value / gridSize) * 10) / 10) * gridSize;
    };

    const newX =
      currentMarketComponent.position?.x !== undefined
        ? parseFloat(
            alignToGrid(
              delta.x / (transformState?.scale ?? 1) +
                currentMarketComponent.position.x,
            ).toFixed(5),
          )
        : undefined;

    const newY =
      currentMarketComponent.position?.y !== undefined
        ? parseFloat(
            alignToGrid(
              delta.y / (transformState?.scale ?? 1) +
                currentMarketComponent.position.y,
            ).toFixed(5),
          )
        : undefined;

    return { newX, newY };
  };

  const isNewPositionInsideDndArea = (
    currentMarketComponent: MarketComponentMarketComponentIO,
    newY: number | undefined,
    newX: number | undefined,
  ) => {
    const market = lsLoad(StorageEnum.Market) as MarketMarketIO;
    const marketY = market?.configuration?.area?.height ?? 0;
    const marketX = market?.configuration?.area?.width ?? 0;
    const y = (newY ?? 0) + (currentMarketComponent.size?.height ?? 0);
    const x = (newX ?? 0) + (currentMarketComponent.size?.width ?? 0);

    return !(y > marketY || y < 0 || x > marketX || x < 0);
  };

  const createMarketComponent = (
    currentMarketComponent: MarketComponentMarketComponentIO,
    marketComponentFoundIndex: number,
    newX: number | undefined,
    newY: number | undefined,
  ): MarketComponentMarketComponentIO => {
    if (marketComponentFoundIndex >= 0) {
      const marketComponent = marketComponents.at(marketComponentFoundIndex);

      if (marketComponent !== undefined) {
        return {
          id: marketComponent.id,
          number: marketComponent.number,
          shortDescription: marketComponent.shortDescription,
          description: marketComponent.description,
          svgTemplate: marketComponent.svgTemplate,
          marketComponentStatus: marketComponent.marketComponentStatus,
          orientation: marketComponent.orientation,
          size: marketComponent.size,
          position: {
            x: newX,
            y: newY,
          },
          marketId: marketComponent.marketId,
          articleTypeId: marketComponent.articleTypeId,
          articleType: marketComponent.articleType,
          typeId: marketComponent.typeId,
          switchToYellowAfterXDays: marketComponent.switchToYellowAfterXDays,
          switchToRedAfterXDays: marketComponent.switchToRedAfterXDays,
          daysBeforeSwitchToRed: marketComponent.daysBeforeSwitchToRed,
          switchToRedAfterDate: marketComponent.switchToRedAfterDate,
          textLength: marketComponent.textLength,
        };
      }
    }

    return {
      id: calculateNextMarketComponentId(),
      number: parseNextNumber(marketComponents, currentMarketComponent),
      shortDescription: currentMarketComponent.shortDescription,
      description: currentMarketComponent.description,
      svgTemplate: currentMarketComponent.svgTemplate,
      marketComponentStatus: currentMarketComponent.marketComponentStatus,
      orientation: currentMarketComponent.orientation,
      size: currentMarketComponent.size,
      position: {
        x: newX,
        y: newY,
      },
      marketId: currentMarketComponent.marketId,
      articleTypeId: currentMarketComponent.articleTypeId,
      articleType: currentMarketComponent.articleType,
      typeId: currentMarketComponent.typeId,
      switchToYellowAfterXDays: currentMarketComponent.switchToYellowAfterXDays,
      switchToRedAfterXDays: currentMarketComponent.switchToRedAfterXDays,
      daysBeforeSwitchToRed: currentMarketComponent.daysBeforeSwitchToRed,
      switchToRedAfterDate: currentMarketComponent.switchToRedAfterDate,
      textLength: currentMarketComponent.textLength,
    };
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { delta, active } = event;
    const currentMarketComponent: MarketComponentMarketComponentIO | null =
      active.data.current?.details;

    if (!currentMarketComponent) {
      return false;
    }

    const { newX, newY } = calculateNewPositionOfMarketComponent(
      currentMarketComponent,
      delta,
    );

    if (!isNewPositionInsideDndArea(currentMarketComponent, newY, newX)) {
      return false;
    }

    const marketComponentFoundIndex = marketComponents.findIndex(
      (item) => item.id === currentMarketComponent.id,
    );

    const marketComponent = createMarketComponent(
      currentMarketComponent,
      marketComponentFoundIndex,
      newX,
      newY,
    );

    if (hasCollision(marketComponent, marketComponents)) {
      return false;
    }

    if (marketComponentFoundIndex >= 0) {
      marketComponents.splice(marketComponentFoundIndex, 1);
    }

    addMarketComponent(marketComponent);
  };

  const handleDialogOpenDetails = (
    marketComponent: MarketComponentMarketComponentIO,
  ) => {
    setShowDetails(true);
    setCurrentMarketComponent(marketComponent);
  };

  const handleDialogOpenEdit = (
    marketComponent: MarketComponentMarketComponentIO,
  ) => {
    setShowDetails(false);
    setCurrentMarketComponent(marketComponent);
  };

  const handleDialogClose = () => {
    setCurrentMarketComponent(undefined);
  };

  const calculateNextMarketComponentId = useCallback(() => {
    return Math.max(...marketComponents.map((c) => c.id ?? 0)) + 1;
  }, [marketComponents]);

  useEffect(() => {
    if (!marketData?.length) return;

    const firstMarket = marketData[0];
    if (!firstMarket?.id) return;

    lsSave(StorageEnum.MarketComponents, marketData);
    initMarket(marketData);

    // eslint-disable-next-line
    setCurrentMarket((prev) => {
      if (prev) return prev;
      lsSave(StorageEnum.Market, firstMarket);
      return firstMarket.id!.toString();
    });
  }, [marketData, currentMarket, initMarket]);

  // Sync API data into local state only when NOT in edit mode.
  // While editing, local state is source of truth until "Finish" saves to API.
  // Otherwise a refetch (e.g. after broadcast) would overwrite drag-and-drop changes.
  useEffect(() => {
    if (isInEditMode || !marketComponentsData) return;
    lsSave(StorageEnum.MarketComponents, marketComponentsData);
    initMarketComponents(marketComponentsData);
  }, [marketComponentsData, initMarketComponents, isInEditMode]);

  useEffect(() => {
    if (!marketComponent) return;

    const newId = calculateNextMarketComponentId();
    const last = marketComponents[marketComponents.length - 1];

    const base: typeof marketComponent = {
      ...marketComponent,
      id: newId,
      marketComponentStatus:
        MarketComponentMarketComponentIOMarketComponentStatusEnum._252525,
      shortDescription: t("ui.newMarketComponent"),
      description: t("ui.newMarketComponentDescription"),
      articleTypeId: last?.articleTypeId ?? marketComponent.articleTypeId,
      articleType: last?.articleType ?? marketComponent.articleType,
      switchToYellowAfterXDays:
        last?.switchToYellowAfterXDays ??
        marketComponent.switchToYellowAfterXDays,
      switchToRedAfterXDays:
        last?.switchToRedAfterXDays ?? marketComponent.switchToRedAfterXDays,
      daysBeforeSwitchToRed:
        last?.daysBeforeSwitchToRed ?? marketComponent.daysBeforeSwitchToRed,
      orientation: last?.orientation ?? marketComponent.orientation,
    };

    const nextNumber = parseNextNumber(marketComponents, base);

    const updated = {
      ...base,
      number: nextNumber,
      shortDescription: nextNumber?.toString(),
    };

    if (
      !hasCollision(updated, marketComponents) &&
      isNewPositionInsideDndArea(
        updated,
        updated.position?.y,
        updated.position?.x,
      )
    ) {
      addMarketComponent(updated);
      // eslint-disable-next-line
      setShowDetails(false);
      setCurrentMarketComponent(updated);
      addIdOfMarketComponents(newId);
    }

    setMarketComponent(null);
  }, [
    marketComponent,
    addIdOfMarketComponents,
    calculateNextMarketComponentId,
    marketComponents,
    setMarketComponent,
    addMarketComponent,
    t,
  ]);

  useEffect(() => {
    const updateMarket = () => {
      if (lsLoad(StorageEnum.Market)?.id) {
        setCurrentMarket(lsLoad(StorageEnum.Market).id.toString());
      }
    };

    window.addEventListener("storage", updateMarket);

    return () => {
      window.removeEventListener("storage", updateMarket);
    };
  });

  return {
    marketComponents,
    isDetailsDialogOpen,
    isEditDialogOpen,
    currentMarketComponent,
    enableZoom,
    loading: marketLoading || marketComponentsLoading,
    handleDragEnd,
    removeMarketComponent,
    handleDialogOpenDetails,
    handleDialogOpenEdit,
    handleDialogClose,
  };
};
